import React from "react";
import { useLocationSuggestions } from "@/hooks/useLocationSuggestions";
import ExperiencesContent from "@tripbuilder/cat-experiences/experiencesContent";
import AccommodationRhs from "@tripbuilder/cat-accommodation/right/AccommodationRHS";
import useTripStore from "@/stores/tripBuilder/useTrip";
import OverviewTab from "@tripbuilder/cat-overview/right";
import TripItinerary from "@tripbuilder/cat-origin/right";
import { Location } from "@/types";

interface RhsContentProps {
  activeTab: string;
  switchToSchedule: () => void;
  tripData: any;
  isAddDestinationModalOpen: boolean;
  handleTransportButtonClick: (location: Location) => void;
  setIsModalOpen: (isOpen: boolean) => void;
}

interface EnhancedWelcomeViewProps {
  setIsModalOpen: (isOpen: boolean) => void;
}

const EnhancedWelcomeView: React.FC<EnhancedWelcomeViewProps> = ({
  setIsModalOpen,
}) => {
  // const user = useAuthStore((state) => state.user);
  const tripData = useTripStore((state) => state.tripData);
  const { data: suggestionData, isLoading: isSuggestionsLoading } =
    useLocationSuggestions(tripData?.name || "", tripData?.locations || []);

  const getFlagImage = (countryName: string) => {
    const countryKey = countryName.toLowerCase().replace(/\s/g, "");
    return `/assets/worldFlags/${countryKey}.png`;
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-8 my-4 bg-neutral-50 rounded-xl">
      <div className="flex flex-col items-center space-y-8 max-w-md text-center">
        <div className="relative">
          <div className="absolute inset-0 bg-indigo-100 rounded-full blur-xl opacity-50"></div>
          <div className="relative px-[20px] py-4 bg-gradient-to-tr from-indigo-600 to-blue-600 rounded-full shadow-lg">
            <i className="fa-solid fa-earth-americas text-white text-3xl animate-spin-slow"></i>
          </div>
        </div>

        <div className="space-y-4">
          <h1 className="text-2xl font-semibold text-neutral-800 tracking-tight">
            Start Your Journey
          </h1>
          <p className="text-neutral-600 leading-relaxed text-[15px] font-light max-w-sm">
            Create your perfect trip itinerary by adding your first destination.
            We'll help you plan every detail of your adventure.
          </p>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          className="group px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
        >
          <span className="text-[14px] font-medium">Add Destination</span>
          <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center group-hover:translate-x-0.5 transition-transform duration-200">
            <i className="fa-solid fa-arrow-right text-[12px]"></i>
          </div>
        </button>

        <div className="w-full pt-3">
          <div className="flex items-center justify-center space-x-2 mb-8">
            <h2 className="text-md text-center font-medium text-neutral-700">
              Popular Destinations
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {(!isSuggestionsLoading && suggestionData?.suggestions
              ? [...suggestionData.suggestions].slice(0, 4)
              : Array(4).fill(null)
            ).map((suggestion, index) => (
              <div
                key={suggestion?.waytora_id || index}
                // onClick={() => handleAddLocation(suggestion)}
                className={`group rounded-lg shadow-sm border border-neutral-100 bg-white/50 backdrop-blur-sm overflow-hidden transition-all duration-200 p-3 cursor-pointer ${
                  suggestion
                    ? "hover:border-indigo-100 hover:bg-white/80"
                    : "animate-pulse"
                }`}
              >
                {suggestion ? (
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-sm leading-tight mb-1 text-left">
                        {suggestion.name}
                      </h3>
                      <p className="text-xs font-light text-neutral-500 text-left">
                        {suggestion.address_components.country}
                      </p>
                    </div>
                    <img
                      src={getFlagImage(suggestion.address_components.country)}
                      alt={`${suggestion.address_components.country} flag`}
                      className="w-6 h-6 rounded-full object-cover ring-1 ring-neutral-100"
                    />
                  </div>
                ) : (
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="h-4 bg-neutral-100 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-neutral-100 rounded w-1/2" />
                    </div>
                    <div className="w-6 h-6 rounded-full bg-neutral-100" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const RhsContent: React.FC<RhsContentProps> = ({
  activeTab,
  switchToSchedule,
  tripData,
  isAddDestinationModalOpen,
  handleTransportButtonClick,
  setIsModalOpen,
}) => {
  const { selectedLocation } = useTripStore();

  if (!selectedLocation) return <div>Loading trip data...</div>;

  if (!selectedLocation.origin) {
    return (
      <div
        id="rhsContent"
        className="w-full h-screen flex flex-col items-center justify-start space-y-4"
      >
        {(activeTab === "overview" || activeTab === "tora") && (
          <OverviewTab
            switchToSchedule={switchToSchedule}
            handleTransportButtonClick={handleTransportButtonClick}
            setIsModalOpen={setIsModalOpen}
          />
        )}
        {activeTab === "schedule" && (
          <ExperiencesContent
            isAddDestinationModalOpen={isAddDestinationModalOpen}
          />
        )}
        {activeTab === "accommodation" && <AccommodationRhs />}
      </div>
    );
  }

  // Origin location view (simplified to just show itinerary)
  return (
    <div id="rhs-overview" className="w-full h-screen flex flex-col">
      <div className="flex-1 w-full h-full pt-4">
        {tripData?.locations?.length > 1 ? (
          <TripItinerary
            selectedTrip={tripData}
            handleTransportButtonClick={handleTransportButtonClick}
          />
        ) : (
          <EnhancedWelcomeView setIsModalOpen={setIsModalOpen} />
        )}
      </div>
    </div>
  );
};

export default RhsContent;
